<template>
  <AccordionCard
    v-show="!cardSelected || (isEditing && isCollapsed)"
    :hideChevrons="true"
    :isCollapsed="isCollapsed">
    <template slot="title">
      Sales Flyer PDF
      <span
        class="kt-badge kt-badge--danger kt-badge--inline kt-badge--pill kt-badge--rounded ml-3"
        v-tooltip.right-end="{ content: 'Software may contain serious errors, and any resulting instability could cause crashes or data loss. Please not use for commercial purpose.', offset: 5 }"
        v-if="false">Beta
      </span>
    </template>
    <template slot="buttons">
      <button
        type="button"
        class="btn btn-md btn-outline-hover-secondary kt-label-font-color-2--off text-danger"
        @click="downloadPDF()"
        :disabled="isPDFDownloadingHQ || isPDFDownloadingLQ || !isCompletedQuote"
        v-if="!isCollapsed">
        <span v-if="!isPDFDownloadingHQ">
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" /> Download <span v-if="enableHQAndHL">(HQ)</span>
        </span>
        <span v-else>
          <font-awesome-icon :icon="['fas', 'spinner']" class="mr-1" spin /> Generating PDF <span v-if="enableHQAndHL">(HQ)</span>
        </span>
      </button>
      <button
        type="button"
        class="btn btn-md btn-outline-hover-secondary kt-label-font-color-2--off text-danger"
        @click="downloadPDF({ compressed: true })"
        :disabled="isPDFDownloadingHQ || isPDFDownloadingLQ || !isCompletedQuote"
        v-if="enableHQAndHL && !isCollapsed">
        <span v-if="!isPDFDownloadingLQ">
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" /> Download (LQ)
        </span>
        <span v-else>
          <font-awesome-icon :icon="['fas', 'spinner']" class="mr-1" spin /> Generating PDF (LQ)
        </span>
      </button>
      <FlyerPreviewButton
        v-if="!isEditing || flyerPdf"
        :on-loaded="loadPreviewPDF"
      />
      <button
        class="btn btn-outline-brand btn-outline-danger btn-elevate btn-circle btn-icon btn-sm"
        @click="() => {emitSelectAccordionCard('flyer-pdf');resetSelectedSections();}"
        :disabled="isEditing || isPDFDownloadingHQ || isPDFDownloadingLQ || !isCompletedQuote"
        v-if="!isCollapsed">
        <font-awesome-icon :icon="['fas', 'pen']"/>
      </button>
      <button
        class="btn btn-md btn-outline-hover-secondary kt-label-font-color-2"
        :disabled="isSaving"
        @click="cancelPDF"
        v-if="isCollapsed && !flyerPdf">
        <font-awesome-icon :icon="['fas', 'times']" class="mr-1" /> Cancel
      </button>
      <button
        class="btn btn-sm btn-wide btn-primary"
        :disabled="isSaving"
        @click="save"
        v-if="isCollapsed && !flyerPdf">
        <font-awesome-icon :icon="['fas', 'check']" class="mr-1" v-if="!isSaving" />
        <font-awesome-icon :icon="['fas', 'spinner']" spin class="mr-1" v-if="isSaving" /> Done
      </button>
    </template>

    <div v-if="canEditFlyerTemplateOption || flyerPdf" class="mt-2">
      <div class="carousel__container" v-if="flyerPdf">
        <PDFViewer :data="flyerPdf" />
      </div>
      <div v-else>
        <a
          href=""
          role="tab"
          class="nav-link"
        >
          <strong style="font-weight: 600;">Select template</strong>
        </a>

        <div class="carousel__container">
          <div
            v-for="template in templates"
            :key="template.id"
            :class="[
              flyerOption === template.id ? 'active' : '',
              'border-radius-20 is-flex p-2 w-40 template__card',
            ]"
          >
            <picture class="PhotoImage sideBar">
              <img :src="template.image" :alt="template.name" />
              <input type="radio"
                v-model="flyerOption"
                :id="template.name"
                :value="template.id"
                class="card__content"
              />

              <label :for="template.name">{{ template.name }}</label>
            </picture>
          </div>
        </div>
      </div>
    </div>

    <ul v-if="!flyerPdf" class="nav nav-tabs nav-tabs-line nav-tabs-bold nav-tabs-line-3x nav-tabs-line-info mb-4" role="tablist">
      <li class="nav-item">
        <a
          href=""
          role="tab"
          class="nav-link"
          :class="{ 'active': selectedTab === tabs.SELECT_IMAGES_TAB }"
          @click.prevent="selectTab(tabs.SELECT_IMAGES_TAB)">&nbsp;&nbsp;Select images&nbsp;&nbsp;</a>
      </li>
      <li class="nav-item">
        <a
          href=""
          role="tab"
          class="nav-link"
          :class="{ 'active': selectedTab === tabs.LINE_BREAKS_NUMBER_TAB }"
          @click.prevent="selectTab(tabs.LINE_BREAKS_NUMBER_TAB)">&nbsp;&nbsp;Line breaks number&nbsp;&nbsp;</a>
      </li>
      <li class="nav-item">
        <a
          href=""
          role="tab"
          class="nav-link"
          :class="{ 'active': selectedTab === tabs.SELECT_SECTIONS_TAB }"
          @click.prevent="selectTab(tabs.SELECT_SECTIONS_TAB)">&nbsp;&nbsp;Sections&nbsp;&nbsp;</a>
      </li>
    </ul>

    <div v-if="!flyerPdf" class="container-fluid" v-show="selectedTab === tabs.SELECT_IMAGES_TAB">
      <div class="row my-5">
        <div class="form-group col-md-2">
          <label class="labelTitle">Select your cover image(s)</label>
        </div>
        <div class="col-md-10">
          <ImagePicker
            v-if="countries"
            :isDuplicated="false"
            :imageUrl="getCoverImageUrl"
            :isEditing="true"
            :total="totalImages"
            :images="images"
            :countries="countries"
            :isSearching="isImagePickerSearching"
            :defaultParams="defaultImagePickerParams"
            :limitMinOfSelectedImages="limitMinOfSelectedImagesCover"
            :limitMaxOfSelectedImages="limitMaxOfSelectedImagesCover"
            :labelSearch="`Select ${limitMaxOfSelectedImagesCover} image(s)`"
            :labelButton="`Select ${limitMaxOfSelectedImagesCover} image(s)`"
            :disableButton="isSaving"
            @onSearch="onImagePickerSearch"
            @onSelectImage="onImagePickerSelectCoverImage"
            style="width: 150px;height: auto;" />
            <div class="text-danger my-3" v-if="hasErrorsCover">This field is required and It needs to select {{ limitMaxOfSelectedImagesCover }} image(s).</div>
        </div>
      </div>
      <div class="row">
        <div class="form-group col-md-2">
          <label class="labelTitle">Select your image(s) template</label>
        </div>
        <div class="col-md-10">
          <ImagePicker
            v-if="countries"
            :isDuplicated="false"
            :imageUrl="getTemplateImageUrl"
            :isEditing="true"
            :total="totalImages"
            :images="images"
            :countries="countries"
            :isSearching="isImagePickerSearching"
            :defaultParams="defaultImagePickerParams"
            :limitMinOfSelectedImages="limitMinOfSelectedImagesTemplate"
            :limitMaxOfSelectedImages="limitMaxOfSelectedImagesTemplate"
            :labelSearch="`Select ${limitMaxOfSelectedImagesTemplate} image(s)`"
            :labelButton="`Select ${limitMaxOfSelectedImagesTemplate} image(s)`"
            :disableButton="isSaving"
            @onSearch="onImagePickerSearch"
            @onSelectImage="onImagePickerSelectTemplateImages"
            style="width: 150px;height: auto;" />
            <div class="text-danger my-3" v-if="hasErrorsTemplate">This field is required and It needs to select {{ limitMaxOfSelectedImagesTemplate }} image(s).</div>
        </div>
      </div>
    </div>

    <div v-if="!flyerPdf" class="container-fluid" v-show="selectedTab === tabs.LINE_BREAKS_NUMBER_TAB">
      <TabShareLineBreaksNumber
        :data="dayList"
        :isSaving="isSaving" />
    </div>

    <div v-if="!flyerPdf" class="container-fluid" v-show="selectedTab === tabs.SELECT_SECTIONS_TAB">
      <p class="my-2 mb-4">Please select the section to be included into the quote for the Sales Flyer PDF, Landing Page and Guest Documentation</p>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.cover.active"
            checked
            disabled
          > Cover <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input type="checkbox" v-model="selectedSections.itinerary.active"
          checked
          disabled
          > Itinerary <span></span>
        </label>
      </div>
      <div v-if="!isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.inclusions.active"
          > Inclusions <span></span>
        </label>
      </div>
      <div v-if="!isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
          type="checkbox"
          v-model="selectedSections.termsConditions.active"
          > Terms & Conditions <span></span>
        </label>
      </div>
      <div v-if="isTrafalgarRiver" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.map.active"
          > Map <span></span>
        </label>
      </div>
    </div>
  </AccordionCard>
</template>

<script>
import { mapGetters } from 'vuex'
import NProgress from 'nprogress'
import cloneDeep from 'lodash.clonedeep'

import { buildNewAssets, getValidatedImages, getValidatedExportSection } from '@/helpers'
import {
  OPPORTUNITY_EXPORT_TYPES,
  OPPORTUNITY_EXPORT_SECTIONS_IDS,
  CUSTOM_SALES_FLYER_PDF_TEMPLATES,
  SCHEDULED_SALES_FLYER_PDF_TEMPLATES,
  OPPORTUNITY__TYPES,
  OPPORTUNITY__BRANDS,
  TRAFALGAR_RIVER_SALES_FLYER_PDF_TEMPLATES
} from '@/constants'


import AccordionCard from '@/components/_shared/accordion/AccordionCard.vue'
import ImagePicker from '@/components/_shared/image-picker/ImagePicker.vue'
import TabShareLineBreaksNumber from '@/components/opportunity-edit/tab-share-line-breaks-number/TabShareLineBreaksNumber.vue'
import FlyerPreviewButton from "@/components/opportunity-edit/FlyerPreviewButton.vue";
import PDFViewer from "@/components/_shared/PDFViewer.vue";

export default {
  name: 'TabShareFlyerPdf',
  data () {
    const SELECT_IMAGES_TAB = 'select-images-tab'
    const LINE_BREAKS_NUMBER_TAB = 'line-breaks-number-tab'
    const SELECT_SECTIONS_TAB= 'select-sections-tab'

    const templateContentType = 'flyer_pdf'

    const IDS_BRANDS_SELECT_SECTIONS = [13, OPPORTUNITY__BRANDS.TRAFALGAR_RIVER]

    return {
      flyerPdf: '',
      IDS_BRANDS_SELECT_SECTIONS,
      isPDFDownloadingHQ: false,
      isPDFDownloadingLQ: false,
      coverImageSelected: null,
      templateImagesSelected: null,
      images: [],
      totalImages: 0,
      isImagePickerSearching: false,
      limitMinOfSelectedImagesCover: 1,
      limitMaxOfSelectedImagesCover: 4,
      limitMinOfSelectedImagesTemplate: 2,
      limitMaxOfSelectedImagesTemplate: 4,
      isSaving: false,
      hasErrorsCover: false,
      hasErrorsTemplate: false,
      selectedTab: SELECT_IMAGES_TAB,
      tabs: {
        SELECT_IMAGES_TAB,
        LINE_BREAKS_NUMBER_TAB,
        SELECT_SECTIONS_TAB
      },
      dayList: [],
      enableHQAndHL: true,
      templateContentType,
      selectedSections: this.getValidatedSelectedSections(),
      flyerOption: this.opportunityData && this.opportunityData.consumer_section && this.opportunityData.consumer_section.flyer_option || 1,
    }
  },
  components: {
    PDFViewer,
    FlyerPreviewButton,
    AccordionCard,
    ImagePicker,
    TabShareLineBreaksNumber
  },
  watch: {
    opportunityData: {
      handler (newValue) {
        this.flyerOption = newValue.consumer_section.flyer_option || 1
      },
      immediate: true
    },
  },
  computed: {
    ...mapGetters({
      'locationCountries': 'location/countries',
      'opportunityData': 'opportunity/opportunity',
      'itinerarySourceTypeSelected': 'opportunity/itinerarySourceTypeSelected'
    }),
    isTrafalgarRiver() {
      return this.opportunityData.opportunity_details.id_brand === OPPORTUNITY__BRANDS.TRAFALGAR_RIVER;
    },
    templates () {
      if (this.opportunityData.properties_opportunity.id_brand === OPPORTUNITY__BRANDS.TRAFALGAR_RIVER)
        return TRAFALGAR_RIVER_SALES_FLYER_PDF_TEMPLATES;

      return this.opportunityData.properties_opportunity.id_opportunity_export_type === OPPORTUNITY__TYPES.CUSTOM
        ? CUSTOM_SALES_FLYER_PDF_TEMPLATES
        : SCHEDULED_SALES_FLYER_PDF_TEMPLATES
    },
    countries () {
      if (Array.isArray(this.locationCountries) && this.locationCountries.length > 0) {
        return this.locationCountries
      }
      return []
    },
    defaultImagePickerParams () {
      return {
        limit: 21,
        offset: 0,
        w: 150,
        h: 150,
        country: '',
        city: ''
      }
    },
    getCoverImageUrl () {
      if (
        this.coverImageSelected &&
        Array.isArray(this.coverImageSelected) &&
        this.coverImageSelected.length > 0) {
        return this.coverImageSelected[0].url_image
      }
      if (this.data &&
          Array.isArray(this.data.cover_page) &&
          this.data.cover_page.length > 0) {
        return this.data.cover_page[0].url_image
      }
      return ''
    },
    getTemplateImageUrl () {
      if (
        this.templateImagesSelected &&
        Array.isArray(this.templateImagesSelected) &&
        this.templateImagesSelected.length > 0) {
        return this.templateImagesSelected[0].url_image
      }
      if (this.data &&
          Array.isArray(this.data.images_page) &&
          this.data.images_page.length > 0) {
        return this.data.images_page[0].url_image
      }
      return ''
    },
    canEditFlyerTemplateOption() {
      const ALLOWED_BRANDS = [OPPORTUNITY__BRANDS.TRAFALGAR, OPPORTUNITY__BRANDS.TRAFALGAR_RIVER, OPPORTUNITY__BRANDS.CONTIKI]
      return ALLOWED_BRANDS.includes(this.opportunityData.properties_opportunity.id_brand)
    }
  },
  props: {
    cardSelected: {
      type: String,
      required: true
    },
    isCollapsed: {
      type: Boolean,
      required: true
    },
    isEditing: {
      type: Boolean,
      required: true
    },
    isCompletedQuote: {
      type: Boolean,
      default: false
    },
    data: {
      type: [Array, Object],
      required: false
    },
    templateSetting: {
      type: [Array, Object],
      required: false,
    }
  },
  methods: {
    resetSelectedSections () {
      this.selectedSections = this.getValidatedSelectedSections()
    },
    getValidatedSelectedSections() {
      return {
        dates: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.DATES),
        cover: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.COVER),
        letter: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.LETTER),
        itinerary: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.ITINERARY),
        inclusions: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.INCLUSIONS),
        map: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.MAP),
        pricing: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.PRICING),
        termsConditions: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.TERMS_CONDITIONS),
        bookMyGroup: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.BOOK_MY_GROUP),
        contactUs: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.CONTACT_US)
      }
    },
    validateExportSection(exportSectionId = 0) {
      return getValidatedExportSection(this.templateSetting, exportSectionId, OPPORTUNITY_EXPORT_TYPES.SALES_FLYER_PDF.name)
    },
    populateDayList (contentType) {
      if (this.opportunityData) {
        this.dayList = []
        this.dayList = this.opportunityData.itineraries[this.itinerarySourceTypeSelected.index].days.map(d => {
          const templateContentDaysData = this.opportunityData.template_content
          let templateContentDays = [{ id: null, type: contentType, days: [] }]
          if (templateContentDaysData) {
            templateContentDays = templateContentDaysData.find(t => t.type === contentType)
          }
          let dayFound = { line_breaks_number: 0 }
          if (templateContentDays && Array.isArray(templateContentDays.days) && templateContentDays.days.length > 0) {
            dayFound = templateContentDays.days.find(cd => cd.startday === d.startday)
            if (!dayFound) {
              dayFound = { line_breaks_number: 0 }
            }
          }
          return {
            id: d.id,
            title: d.title,
            startday: d.startday,
            line_breaks_number: Number(dayFound.line_breaks_number)
          }
        })
      }
    },
    selectTab (tabName) {
      this.populateDayList(this.templateContentType)
      this.selectedTab = tabName
    },
    isItineraryValid () {
      console.log(this.itinerarySourceTypeSelected.index)
      const validation = this.opportunityData.itineraries[this.itinerarySourceTypeSelected.index].days.filter(d => d.id === null)
      return !(Array.isArray(validation) && validation.length > 0)
    },
    loadPreviewPDF (data) {
      if (this.flyerPdf !== data) {
        if (this.flyerPdf) this.$emit('onEditing')
        this.emitSelectAccordionCard('flyer-pdf')

        this.flyerPdf = data
      }
    },
    emitSelectAccordionCard (cardName) {
      if (!this.isItineraryValid()) {
        this.$notification.warning('You have pending changes in your itinerary, please save before entry this section.', 9000)
        return false
      }
      this.$emit('onSelect', cardName)
    },
    downloadPDF (options = { compressed: false }) {
      const uuid = this.$route.params.privateId

      let payload = {
        uuid,
        compressed: options.compressed,
        sourceType: `ITP-FLYER`,
        flyer_option: this.flyerOption || '',
      }

      this.$emit('onEditing')

      this.isPDFDownloadingHQ = !options.compressed
      this.isPDFDownloadingLQ = options.compressed

      this.$store
        .dispatch('opportunity/downloadFlyerPDF', payload)
        .then(response => {
          this.isPDFDownloadingHQ = false
          this.isPDFDownloadingLQ = false
          this.$notification.success('Download complete!')
          this.$emit('onEditing')
        })
        .catch(error => {
          console.log(error)
          this.isPDFDownloadingHQ = false
          this.isPDFDownloadingLQ = false
          this.$notification.error('Download failed')
          this.$emit('onEditing')
        })
    },
    onImagePickerSearch (params) {
      if (params.country === '' && params.city === '') {
        this.isImagePickerSearching = false
        this.totalImages = 0
        this.images = []
        return false
      }
      this.isImagePickerSearching = true
      this.$store.dispatch('asset/getAssets', params)
        .then(response => {
          this.images = response.asset // Array images
          this.totalImages = response.total
          this.isImagePickerSearching = false
        })
        .catch(error => {
          console.log(error)
          this.isImagePickerSearching = false
          this.totalImages = 0
          this.images = []
        })
    },
    onImagePickerSelectCoverImage (imageSelected) {
      this.coverImageSelected = imageSelected
    },
    onImagePickerSelectTemplateImages (imagesSelected) {
      this.templateImagesSelected = imagesSelected
    },
    cancelPDF () {
      this.coverImageSelected = null
      this.templateImagesSelected = null
      this.resetSelectedSections()
      this.emitSelectAccordionCard('')
      this.selectedTab = this.tabs.SELECT_IMAGES_TAB
      this.pdfData = ''
    },
    isValidBrandForChoosingSections () {
      const idBrand = this.opportunityData.opportunity_details.id_brand
      const brandFound = this.IDS_BRANDS_SELECT_SECTIONS.includes(idBrand)
      return brandFound;
    },
    save () {
      this.$store.commit('opportunity/TEMP_SAVE_FLYER_OPTION', { flyer_option: this.flyerOption })

      if (this.selectedTab === this.tabs.SELECT_IMAGES_TAB) {
        this.saveSelectImagesTab()
      } else if (this.selectedTab === this.tabs.LINE_BREAKS_NUMBER_TAB) {
        this.saveLineBreaksNumberTab(this.templateContentType)
      } else if (this.selectedTab === this.tabs.SELECT_SECTIONS_TAB) {
        this.saveSelectSectionsTab()
      }
    },
    getUpdatedSelectedSectionsByExportType (currentContents = []) {
      return Object.values(this.selectedSections).map(section => {
        const currentContent = currentContents.find(content => content.id_content === section.id_content)
        return {
          ...section,
          id: currentContent !== undefined ? currentContent.id : null,
          status: section.active ? 1 : -1
        }
      }).filter(section => !(section.status === -1 && [undefined, null].includes(section.id)));
    },
    saveSelectSectionsTab () {
      if (!this.isValidBrandForChoosingSections()) return this.$notification.warning('Feature not available for this brand.', 5000);

      const pastOpportunitySettings = this.templateSetting || [];

      const salesFlyerPdfExists = pastOpportunitySettings.some(opportunitySetting =>
        opportunitySetting.type === OPPORTUNITY_EXPORT_TYPES.SALES_FLYER_PDF.name
      );
      const guestDocumentationExists = pastOpportunitySettings.some(opportunitySetting =>
        opportunitySetting.type === OPPORTUNITY_EXPORT_TYPES.GUEST_DOCUMENTATION.name
      );
      const landingPageExists = pastOpportunitySettings.some(opportunitySetting =>
        opportunitySetting.type === OPPORTUNITY_EXPORT_TYPES.LANDING_PAGE.name
      );

      const adaptedTemplateSections = pastOpportunitySettings.map(opportunitySetting => {
        if (opportunitySetting.type === OPPORTUNITY_EXPORT_TYPES.SALES_FLYER_PDF.name && salesFlyerPdfExists) {
          return {
            ...opportunitySetting,
            contents: this.getUpdatedSelectedSectionsByExportType(opportunitySetting.contents),
            id: opportunitySetting.id
          };
        }
        if (opportunitySetting.type === OPPORTUNITY_EXPORT_TYPES.GUEST_DOCUMENTATION.name && guestDocumentationExists) {
          return {
            ...opportunitySetting,
            contents: this.getUpdatedSelectedSectionsByExportType(opportunitySetting.contents),
            id: opportunitySetting.id
          };
        }
        if (opportunitySetting.type === OPPORTUNITY_EXPORT_TYPES.LANDING_PAGE.name && landingPageExists) {
          return {
            ...opportunitySetting,
            contents: this.getUpdatedSelectedSectionsByExportType(opportunitySetting.contents),
            id: opportunitySetting.id
          };
        }
        return opportunitySetting;
      });

      if (!salesFlyerPdfExists) {
        adaptedTemplateSections.push({
          id: null,
          type: OPPORTUNITY_EXPORT_TYPES.SALES_FLYER_PDF.name,
          contents: this.getUpdatedSelectedSectionsByExportType([]),
        });
      }
      if (!guestDocumentationExists) {
        adaptedTemplateSections.push({
          id: null,
          type: OPPORTUNITY_EXPORT_TYPES.GUEST_DOCUMENTATION.name,
          contents: this.getUpdatedSelectedSectionsByExportType([]),
        });
      }
      if (!landingPageExists) {
        adaptedTemplateSections.push({
          id: null,
          type: OPPORTUNITY_EXPORT_TYPES.LANDING_PAGE.name,
          contents: this.getUpdatedSelectedSectionsByExportType([]),
        });
      }

      let newOpportunityData = cloneDeep(this.opportunityData)
      newOpportunityData.opportunity_settings = adaptedTemplateSections

      this.$store.commit('opportunity/TEMP_SAVE_OPPORTUNITY_SETTINGS', adaptedTemplateSections )

      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$notification.warning('Now try to preview your changes.', 5000)
            this.emitSelectAccordionCard('')
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then((response) => {
                this.isSaving = false
                NProgress.done()
                this.selectedTab = this.tabs.SELECT_SECTIONS_TAB
              })
              .catch(error => {
                console.log(error)
              })
          } else {
            this.isSaving = false
            NProgress.done()
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          NProgress.done()
        })
    },
    saveSelectImagesTab () {
      if (!this.coverImageSelected) {
        if (this.data &&
          Array.isArray(this.data.cover_page) &&
          (this.data.cover_page.length >= this.limitMinOfSelectedImagesCover || this.data.cover_page.length <= this.limitMaxOfSelectedImagesCover)) {
          this.coverImageSelected = this.data.cover_page
        }
      }

      if (!this.templateImagesSelected) {
        if (this.data &&
          Array.isArray(this.data.images_page) &&
          (this.data.images_page.length >= this.limitMinOfSelectedImagesTemplate || this.data.images_page.length <= this.limitMaxOfSelectedImagesTemplate)) {
          this.templateImagesSelected = this.data.images_page
        }
      }

      const coverImage = buildNewAssets(this.coverImageSelected);
      const imagesTemplate = buildNewAssets(this.templateImagesSelected);

      const coverImageSize = this.coverImageSelected.length;
      const templateImageSize = this.templateImagesSelected.length;

      const coverPageImagesValidated = getValidatedImages(this.opportunityData.consumer_section.images.cover_page, coverImage, coverImageSize)

      const imagesPageImagesValidated = getValidatedImages(this.opportunityData.consumer_section.images.images_page, imagesTemplate, templateImageSize)

      this.$store.commit('opportunity/TEMP_SAVE_SELECT_IMAGES_FLYER', {
        coverPage: coverPageImagesValidated,
        imagesPage: imagesPageImagesValidated
      })

      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$notification.warning('Now try to download the Sales Flyer PDF with its new settings.', 5000)
            this.emitSelectAccordionCard('')
            this.coverImageSelected = null
            this.templateImagesSelected = null
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then((response) => {
                this.isSaving = false
                NProgress.done()
              })
              .catch(error => {
                if (error.response && error.response.status > 400) { // 401 || 403
                  // this.$store.dispatch('auth/removeToken')
                }
              })
          } else {
            this.isSaving = false
            NProgress.done()
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          NProgress.done()
        })
    },
    saveLineBreaksNumberTab (contentType) {
      const validation = this.dayList.filter(d => d.id === null)
      const days = this.dayList

      if (Array.isArray(validation) && validation.length > 0) {
        this.$notification.warning('Something went wrong while fetching days', 5000)
        return false
      }

      this.$store.commit('opportunity/TEMP_SAVE_LINE_BREAKS_NUMBER', { type: contentType, days })

      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$notification.warning('Now try to preview your changes.', 5000)
            this.emitSelectAccordionCard('')
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then((response) => {
                this.isSaving = false
                NProgress.done()
                this.selectedTab = this.tabs.SELECT_IMAGES_TAB
              })
              .catch(error => {
                if (error.response && error.response.status > 400) { // 401 || 403
                  // this.$store.dispatch('auth/removeToken')
                }
              })
          } else {
            this.isSaving = false
            NProgress.done()
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          NProgress.done()
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.nav-tabs.nav-tabs-line {
  border-bottom: 1px solid #ebedf2 !important;
}

.carousel__container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 1rem;
}

.template__card {
  cursor: pointer;
  margin-bottom: 20px;
  width: 18rem;
}

.template__card.active {
  border: 2px solid #007bff;
}

.template__card picture {
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
}

.template__card picture img {
  width: 100%;
  height: auto;
  object-fit: cover;
  margin-bottom: .5rem;
}

.template__card label {
  text-align: center;
  margin-top: .25rem;
}
</style>
